/** Extra blocks merged into articles for more depth. */

export const depthEnrichments = {
  "system-design": {
    "scalability-basics": [
      {
        "type": "h",
        "text": "Worked mini-estimate"
      },
      {
        "type": "p",
        "text": "Suppose 2M DAU, 20 requests/user/day → 40M requests/day ≈ 460 RPS average. With a 5× peak factor you plan ~2.3K RPS. If one app instance handles 200 RPS comfortably, you need ~12 instances plus headroom for failure — before counting DB capacity. Saying this aloud shows structured thinking."
      },
      {
        "type": "table",
        "headers": [
          "Layer",
          "Scale lever",
          "Typical first bottleneck"
        ],
        "rows": [
          [
            "Edge",
            "CDN / anycast",
            "Origin bandwidth"
          ],
          [
            "App",
            "Horizontal pods",
            "Thread pool / GC / DB pool"
          ],
          [
            "Data",
            "Replicas then shards",
            "Primary write IOPS / locks"
          ],
          [
            "Async",
            "Queues + workers",
            "Poison messages / lag"
          ]
        ]
      }
    ],
    "cap-theorem": [
      {
        "type": "h",
        "text": "Client experience during partitions"
      },
      {
        "type": "p",
        "text": "Spell out what users see: errors, read-only mode, or divergent writes that merge later. Product language beats theorem letters alone."
      }
    ],
    "consistency-models": [
      {
        "type": "h",
        "text": "Choosing a model in an interview"
      },
      {
        "type": "ul",
        "items": [
          "Payments/inventory: strong/linearizable.",
          "Social counters: eventual + approximate.",
          "User profile edits: read-your-writes at minimum."
        ]
      }
    ],
    "availability": [
      {
        "type": "h",
        "text": "Dependency math"
      },
      {
        "type": "p",
        "text": "If you call 10 services synchronously each at 99.9%, naive availability can drop near 99%. Mitigate with timeouts, caches, and optional dependencies."
      }
    ],
    "replication": [
      {
        "type": "h",
        "text": "Read scaling pattern"
      },
      {
        "type": "p",
        "text": "Route heavy analytical/read traffic to replicas; keep read-your-writes on primary or session-consistent routing. Monitor replica lag as a first-class SLO."
      }
    ],
    "partitioning": [
      {
        "type": "h",
        "text": "Plan for rebalancing early"
      },
      {
        "type": "p",
        "text": "Even if v1 is a single DB, define a partition key in the data model so you are not stuck with sequential IDs that resist sharding later."
      }
    ],
    "consistent-hashing": [
      {
        "type": "h",
        "text": "Virtual node count"
      },
      {
        "type": "p",
        "text": "Too few vnodes → imbalance; too many → large membership metadata. Start with tens–hundreds per node and measure key distribution skew."
      }
    ],
    "quorum-and-consensus": [
      {
        "type": "h",
        "text": "Interview clarification"
      },
      {
        "type": "p",
        "text": "If you say \"quorum,\" specify N/W/R. If you say \"Raft,\" you are talking consensus for a replicated log — different tool for different jobs."
      }
    ],
    "leader-election": [
      {
        "type": "h",
        "text": "Lease renewal"
      },
      {
        "type": "p",
        "text": "Leaders renew leases before expiry; followers only elect when lease expires. Clock sync assumptions matter — prefer monotonic lease services (etcd)."
      }
    ],
    "load-balancer": [
      {
        "type": "h",
        "text": "Slow start & connection draining"
      },
      {
        "type": "p",
        "text": "New nodes get traffic gradually; retiring nodes finish in-flight requests. These ops details separate junior from senior design answers."
      }
    ],
    "api-gateway": [
      {
        "type": "h",
        "text": "Aggregation caution"
      },
      {
        "type": "p",
        "text": "Gateways that join five backend calls become mini-orchestrators — harder to cache and debug. Prefer BFF only when client needs justify it."
      }
    ],
    "caching-strategies": [
      {
        "type": "h",
        "text": "TTL vs explicit invalidation"
      },
      {
        "type": "p",
        "text": "TTL is simple and eventually correct; explicit invalidation is fresher but races under concurrent writers. Many systems use both (short TTL safety net)."
      }
    ],
    "redis": [
      {
        "type": "h",
        "text": "Cluster mode mental model"
      },
      {
        "type": "p",
        "text": "Hash slots map keys to nodes; multi-key ops need same slot (hash tags). Cross-slot transactions are restricted — design keys accordingly."
      }
    ],
    "kafka": [
      {
        "type": "h",
        "text": "Disk and retention"
      },
      {
        "type": "p",
        "text": "Kafka is disk-backed; retention by time/size. Plan capacity for peak produce rate × retention, not only consumer lag."
      }
    ],
    "tinyurl-url-shortener": [
      {
        "type": "h",
        "text": "Analytics path"
      },
      {
        "type": "p",
        "text": "Redirect must be fast — enqueue click events asynchronously. Never make users wait on analytics writes."
      }
    ],
    "rate-limiter-service": [
      {
        "type": "h",
        "text": "Clock skew"
      },
      {
        "type": "p",
        "text": "Distributed windows are sensitive to clock skew. Prefer token buckets with Redis server time inside Lua scripts."
      }
    ],
    "news-feed": [
      {
        "type": "h",
        "text": "Celebrity problem summary"
      },
      {
        "type": "p",
        "text": "Always call out fanout explosion for high-follower accounts and propose hybrid push/pull — interviewers listen for this explicitly."
      }
    ],
    "payment-gateway": [
      {
        "type": "h",
        "text": "Ledger first"
      },
      {
        "type": "p",
        "text": "Model money as append-only ledger entries with running balances derived. Updates-in-place on a balance column invite lost updates and audit pain."
      }
    ]
  },
  "lld": {
    "oop-fundamentals": [
      {
        "type": "h",
        "text": "CRC card mini-method"
      },
      {
        "type": "p",
        "text": "For each candidate class write Collaborators and Responsibilities on paper. If responsibilities span unrelated themes, split the class before coding."
      }
    ],
    "solid": [
      {
        "type": "h",
        "text": "Smell → principle map"
      },
      {
        "type": "table",
        "headers": [
          "Smell",
          "Principle"
        ],
        "rows": [
          [
            "God class",
            "SRP"
          ],
          [
            "Giant switch on type",
            "OCP / Strategy"
          ],
          [
            "Subclass breaks callers",
            "LSP"
          ],
          [
            "Fat interface",
            "ISP"
          ],
          [
            "new Concrete() deep in domain",
            "DIP"
          ]
        ]
      }
    ],
    "factory": [
      {
        "type": "h",
        "text": "Registering types"
      },
      {
        "type": "p",
        "text": "A map from enum → supplier keeps factories open for extension. Demonstrate this when adding a new vehicle/payment type mid-interview."
      }
    ],
    "strategy": [
      {
        "type": "h",
        "text": "Combining with Factory"
      },
      {
        "type": "p",
        "text": "Factory selects the Strategy implementation; context remains unaware of concrete classes. Clean story for parking pricing or payment methods."
      }
    ],
    "observer": [
      {
        "type": "h",
        "text": "Event bus caution"
      },
      {
        "type": "p",
        "text": "Global static event buses hide dependencies. Prefer explicit subscription in constructors/composition roots for testability."
      }
    ],
    "parking-lot": [
      {
        "type": "h",
        "text": "Display board as Observer"
      },
      {
        "type": "p",
        "text": "Floors notify a display when free-spot counts change. Nice pattern cameo without over-engineering the core allocation."
      }
    ],
    "bookmyshow": [
      {
        "type": "h",
        "text": "Hold TTL details"
      },
      {
        "type": "p",
        "text": "Hold seats for 5–10 minutes; payment success converts holds; expiry job releases. Unique (show, seat) constraint is the last line of defense."
      }
    ],
    "splitwise": [
      {
        "type": "h",
        "text": "Net balance invariant"
      },
      {
        "type": "p",
        "text": "Sum of all user nets in a group should be ~0 (within rounding). Use integer minor units (cents) to avoid float bugs."
      }
    ]
  },
  "cs-fundamentals": {
    "threads": [
      {
        "type": "h",
        "text": "Scheduling and concurrency models"
      },
      {
        "type": "p",
        "text": "A runnable thread needs a core. With more runnable threads than cores, the OS time-slices them — useful for I/O concurrency, wasteful if every thread burns CPU. That is why server frameworks moved from unbounded thread-per-request to bounded pools and async I/O: you want enough concurrency to hide latency, not enough to thrash the scheduler."
      },
      {
        "type": "h",
        "text": "Shared memory hazards"
      },
      {
        "type": "ul",
        "items": [
          "Races: two threads read-modify-write the same variable without synchronization.",
          "Visibility: without memory barriers/volatile/atomics, one thread may never see another's write.",
          "Deadlocks: lock ordering mistakes across threads sharing multiple mutexes."
        ]
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "Prefer message-passing or immutable data for new designs. Introduce shared mutable state only with a clear owner lock or concurrent structure — and document the invariant.",
        "title": "Practical mental model"
      },
      {
        "type": "h",
        "text": "Green threads, goroutines, and async tasks"
      },
      {
        "type": "p",
        "text": "Many modern runtimes multiplex lightweight tasks onto a smaller set of OS threads (Go goroutines, Java virtual threads, Node's event loop + worker pool). In interviews, say what blocks an OS thread in your stack and how the runtime schedules around it."
      }
    ],
    "processes": [
      {
        "type": "h",
        "text": "Process isolation in production"
      },
      {
        "type": "p",
        "text": "Containers and VMs are process isolation at different layers. A crash or memory corruption in one service process should not take down neighbors — that is why we split blast radii even when deploying on one host."
      },
      {
        "type": "ul",
        "items": [
          "Separate privilege: run as non-root with minimal capabilities.",
          "Resource limits: cgroups/ulimits so one process cannot starve the node.",
          "Supervision: systemd/k8s restarts crashed processes with backoff."
        ]
      }
    ],
    "context-switching": [
      {
        "type": "h",
        "text": "Cost you feel in production"
      },
      {
        "type": "p",
        "text": "High context-switch rates show up as elevated system CPU and worse tail latency. Tools like perf sched or language runtime metrics (run queue time) help confirm. Fixes are usually architectural: fewer blocking threads, batching, or async I/O — not \"make the kernel faster.\""
      },
      {
        "type": "callout",
        "tone": "interview",
        "text": "Mention cache/TLB pollution: after a switch, the new thread pays cold-cache penalties that dominate the register save/restore cost."
      }
    ],
    "cpu-scheduling": [
      {
        "type": "h",
        "text": "Multicore reality"
      },
      {
        "type": "ul",
        "items": [
          "Affinity can help cache locality for busy pollers, but over-pinning hurts balancing.",
          "Realtime priorities are dangerous on shared hosts — a runaway RT task can starve the machine.",
          "Containers add another scheduling layer via cgroup CPU quotas — apps may throttle while host CPU looks free."
        ]
      }
    ],
    "paging-and-virtual-memory": [
      {
        "type": "h",
        "text": "Huge pages & TLB"
      },
      {
        "type": "p",
        "text": "TLB misses are expensive at high allocation rates. Huge pages reduce TLB pressure for large heaps (JVMs, databases) at the cost of flexibility and potential waste. In interviews, connect page faults to latency spikes after deploys (cold caches) or memory leaks (thrashing)."
      }
    ],
    "mutex": [
      {
        "type": "h",
        "text": "Lock best practices"
      },
      {
        "type": "ul",
        "items": [
          "Hold locks briefly; never call slow I/O while holding.",
          "Document lock order for multi-lock sequences.",
          "Prefer concurrent collections / atomics for simple counters.",
          "Measure contention (profilers) before micro-optimizing."
        ]
      }
    ],
    "semaphore": [
      {
        "type": "h",
        "text": "Interview classic — reader/writer sketch"
      },
      {
        "type": "p",
        "text": "You can build a simple RW policy with semaphores, but it is easy to starve writers. Prefer language-provided ReadWriteLock unless asked to implement from primitives. Explain fairness trade-offs if you roll your own."
      }
    ],
    "deadlock": [
      {
        "type": "h",
        "text": "Production mitigation toolkit"
      },
      {
        "type": "ul",
        "items": [
          "Consistent lock ordering across the codebase.",
          "tryLock with timeout + retry/backoff.",
          "Transaction deadlock retries in DB clients.",
          "Watchdogs that dump thread stacks on stuck locks."
        ]
      }
    ],
    "normalization": [
      {
        "type": "h",
        "text": "Practical schema review checklist"
      },
      {
        "type": "ul",
        "items": [
          "Every table has a clear primary key.",
          "Foreign keys (or app-enforced equivalents) for critical relationships.",
          "No repeating groups (phone1, phone2).",
          "Derived fields either documented as denormalized caches or removed."
        ]
      }
    ],
    "indexes": [
      {
        "type": "h",
        "text": "Composite index column order"
      },
      {
        "type": "p",
        "text": "Put equality predicates first, then range columns. A query filtering status = ? AND created_at > ? wants (status, created_at). Reversed order often cannot seek efficiently. Covering indexes add included columns to avoid heap lookups."
      }
    ],
    "joins": [
      {
        "type": "h",
        "text": "ORM footguns"
      },
      {
        "type": "p",
        "text": "N+1 queries look like many small joins/lookups in logs. Fix with joins/eager loading/batched IN queries. In interviews, say you verify with query logs and EXPLAIN, not only ORM convenience."
      }
    ],
    "transactions-and-acid": [
      {
        "type": "h",
        "text": "Application transaction boundaries"
      },
      {
        "type": "p",
        "text": "Start the transaction after validation; keep external HTTP calls outside; commit ASAP. Open transactions across user think-time or remote calls are a common outage cause (lock pileups)."
      }
    ],
    "isolation-levels": [
      {
        "type": "table",
        "headers": [
          "Anomaly",
          "Read Committed",
          "Repeatable Read*",
          "Serializable"
        ],
        "rows": [
          [
            "Dirty read",
            "Prevented",
            "Prevented",
            "Prevented"
          ],
          [
            "Non-repeatable read",
            "Possible",
            "Prevented*",
            "Prevented"
          ],
          [
            "Phantom",
            "Possible",
            "Engine-dependent",
            "Prevented"
          ],
          [
            "Write skew",
            "Possible",
            "Possible under SI",
            "Prevented"
          ]
        ]
      },
      {
        "type": "callout",
        "tone": "tip",
        "text": "Exact RR/SI behavior differs by engine (MySQL vs Postgres). Always name your database when answering."
      }
    ],
    "mvcc": [
      {
        "type": "h",
        "text": "Vacuum / purge ops angle"
      },
      {
        "type": "p",
        "text": "Dead versions accumulate until garbage collection. Autovacuum lag causes bloat and slow queries. Strong answers mention monitoring dead tuples / snapshot age, not only the happy-path snapshot reads."
      }
    ],
    "locks": [
      {
        "type": "h",
        "text": "Application-level lock advice"
      },
      {
        "type": "ul",
        "items": [
          "Prefer DB constraints/transactions over ad-hoc locks when protecting invariants.",
          "For distributed locks, always use TTLs + fencing.",
          "Log lock wait times — they are early contention signals."
        ]
      }
    ],
    "execution-plans": [
      {
        "type": "h",
        "text": "Interview EXPLAIN drill"
      },
      {
        "type": "p",
        "text": "Say: \"I look for sequential scans on large tables, nested loops with huge row estimates, and sorts that spill to disk.\" Then propose an index or rewrite. Bring one real story if you have it."
      }
    ],
    "connection-pooling": [
      {
        "type": "h",
        "text": "Pool exhaustion symptom"
      },
      {
        "type": "p",
        "text": "App threads block acquiring connections while DB CPU is idle — classic misconfig (pool too small) or leak (connections not returned). Metrics on wait time to borrow a connection are essential."
      }
    ],
    "dns": [
      {
        "type": "h",
        "text": "Caching layers"
      },
      {
        "type": "p",
        "text": "Browser cache → OS stub → recursive resolver → authoritative. Any layer can serve stale data within TTL. For blue/green cutovers, plan TTL and keep old stack alive until caches expire."
      }
    ],
    "tcp": [
      {
        "type": "h",
        "text": "TIME_WAIT at scale"
      },
      {
        "type": "p",
        "text": "Short-lived connections can pile TIME_WAIT sockets. Mitigate with connection reuse (HTTP keep-alive, pools), and load balancer idle timeouts aligned with app servers."
      }
    ],
    "udp": [
      {
        "type": "h",
        "text": "Building reliability on UDP"
      },
      {
        "type": "p",
        "text": "QUIC/HTTP3 re-implements connection setup, retransmission, and congestion control atop UDP. In interviews, do not say \"UDP is unreliable so never use it\" — say when you accept loss vs when you add reliability."
      }
    ],
    "tls": [
      {
        "type": "h",
        "text": "Handshake cost"
      },
      {
        "type": "p",
        "text": "TLS handshakes add round trips and CPU (less with 1.3 and session resumption). Terminate at the edge for user traffic; use mTLS inside a mesh when you need service identity."
      }
    ],
    "https": [
      {
        "type": "h",
        "text": "HSTS & cookies"
      },
      {
        "type": "p",
        "text": "HSTS forces HTTPS after first visit; Secure/HttpOnly/SameSite cookies reduce XSS/CSRF impact. Mention these when designing browser auth."
      }
    ],
    "http-http-2-http-3": [
      {
        "type": "h",
        "text": "Practical migration notes"
      },
      {
        "type": "ul",
        "items": [
          "H2 benefits most when many small assets share one connection.",
          "Head-of-line still exists at TCP for H2 — mobile lossy networks love H3.",
          "Proxies must support the protocol end-to-end or you silently fall back."
        ]
      }
    ],
    "rest": [
      {
        "type": "h",
        "text": "Idempotency keys for POST"
      },
      {
        "type": "p",
        "text": "When POST creates payments/orders, accept an Idempotency-Key header and store the first result. That is how you make client retries safe without pretending POST is idempotent by HTTP semantics."
      }
    ],
    "grpc": [
      {
        "type": "h",
        "text": "Streaming patterns"
      },
      {
        "type": "ul",
        "items": [
          "Unary: classic request/response.",
          "Server streaming: one request, many responses (feeds).",
          "Bidi: collaborative / push with backpressure via flow control."
        ]
      }
    ],
    "websocket": [
      {
        "type": "h",
        "text": "Heartbeat & reconnect"
      },
      {
        "type": "p",
        "text": "Send ping/pong to detect half-open NATs; clients reconnect with backoff and resume tokens. Server must handle duplicate connections for the same user cleanly."
      }
    ],
    "essential-commands": [
      {
        "type": "h",
        "text": "One-liners interviewers like"
      },
      {
        "type": "ul",
        "items": [
          "ss -lptn — listening ports + processes",
          "journalctl -u svc --since \"10 min ago\"",
          "df -h + du -sh * for disk",
          "curl -v to see TLS/HTTP failure points"
        ]
      }
    ],
    "process-management": [
      {
        "type": "h",
        "text": "Graceful shutdown checklist"
      },
      {
        "type": "ul",
        "items": [
          "Stop accepting new work (readiness fail).",
          "Drain in-flight requests with a deadline.",
          "Flush logs/metrics.",
          "Exit non-zero if drain fails so orchestrators know."
        ]
      }
    ],
    "memory": [
      {
        "type": "h",
        "text": "Container memory limits"
      },
      {
        "type": "p",
        "text": "When RSS hits cgroup limit, the process is OOM-killed even if the host has RAM. Size heaps below the limit and leave headroom for native memory/buffers."
      }
    ],
    "cpu": [
      {
        "type": "h",
        "text": "Throttling vs saturation"
      },
      {
        "type": "p",
        "text": "Saturation: runnable queue long. Throttling: cgroup quota exhausted. Both increase latency; only metrics tell which. Design autoscaling on the right signal."
      }
    ],
    "disk-i-o": [
      {
        "type": "h",
        "text": "DB disks"
      },
      {
        "type": "p",
        "text": "Separate WAL/data disks when possible; provision IOPS intentionally in cloud volumes; watch for noisy neighbors. Estimation interviews should include IOPS as a first-class limit."
      }
    ],
    "networking-tools": [
      {
        "type": "h",
        "text": "Layered debugging mantra"
      },
      {
        "type": "p",
        "text": "DNS → TCP connect → TLS → Application. Never jump to \"restart the pod\" before knowing which layer failed."
      }
    ],
    "race-conditions": [
      {
        "type": "h",
        "text": "Check-then-act examples"
      },
      {
        "type": "ul",
        "items": [
          "if (!map.contains(k)) map.put(k,v) — use putIfAbsent/compute.",
          "Read balance, then write balance-1 — use atomic update/transaction.",
          "Lazy init without safe publication — use holders or concurrent utilities."
        ]
      }
    ],
    "synchronization": [
      {
        "type": "h",
        "text": "Prefer higher-level APIs"
      },
      {
        "type": "p",
        "text": "BlockingQueues, Futures, and actor mailboxes encode synchronization correctly more often than hand-rolled wait/notify. Use primitives when teaching or when libraries cannot express the constraint."
      }
    ],
    "concurrency-locks": [
      {
        "type": "h",
        "text": "Scoped locking"
      },
      {
        "type": "p",
        "text": "Lock only around the invariant mutation. Copy data out, release, then do I/O. Contended locks with I/O inside are a latency death spiral."
      }
    ],
    "thread-pool": [
      {
        "type": "h",
        "text": "Rejection policies"
      },
      {
        "type": "table",
        "headers": [
          "Policy",
          "Behavior",
          "Use when"
        ],
        "rows": [
          [
            "Abort",
            "Throw",
            "You want fail-fast signals"
          ],
          [
            "CallerRuns",
            "Caller executes task",
            "Natural backpressure"
          ],
          [
            "Discard",
            "Drop silently",
            "Almost never for business work"
          ]
        ]
      }
    ],
    "executors": [
      {
        "type": "h",
        "text": "Instrumentation"
      },
      {
        "type": "p",
        "text": "Export active threads, queue depth, and wait time. Saturation is often visible in queue depth before CPU hits 100%."
      }
    ],
    "async-await": [
      {
        "type": "h",
        "text": "Structured concurrency idea"
      },
      {
        "type": "p",
        "text": "Treat related async tasks as a unit that cancels together on failure. Prevents orphaned tasks leaking work after the request ends."
      }
    ],
    "actors-and-message-passing": [
      {
        "type": "h",
        "text": "Supervision"
      },
      {
        "type": "p",
        "text": "Actor systems restart children on failure with backoff. Your interview answer should mention supervision trees when discussing Erlang/Akka-style designs."
      }
    ],
    "authentication": [
      {
        "type": "h",
        "text": "Session fixation & rotation"
      },
      {
        "type": "p",
        "text": "Rotate session IDs on login; invalidate on logout/password change. Store session hashes server-side if using opaque sessions."
      }
    ],
    "jwt": [
      {
        "type": "h",
        "text": "Refresh token rotation"
      },
      {
        "type": "p",
        "text": "Issue short-lived access JWTs + rotating refresh tokens stored server-side. Reuse of an old refresh token triggers family revoke — industry best practice against theft."
      }
    ],
    "oauth": [
      {
        "type": "h",
        "text": "Common grant choices"
      },
      {
        "type": "table",
        "headers": [
          "Grant",
          "Audience",
          "Notes"
        ],
        "rows": [
          [
            "Auth code + PKCE",
            "User login SPAs/native",
            "Default today"
          ],
          [
            "Client credentials",
            "M2M",
            "No user"
          ],
          [
            "Device code",
            "TVs/CLI",
            "User authorizes elsewhere"
          ]
        ]
      }
    ],
    "rbac": [
      {
        "type": "h",
        "text": "Resource scopes"
      },
      {
        "type": "p",
        "text": "project:123:admin beats a global admin role for multi-tenant systems. Encode tenant boundaries in every authorization check."
      }
    ],
    "caching": [
      {
        "type": "h",
        "text": "Negative caching"
      },
      {
        "type": "p",
        "text": "Cache \"not found\" briefly to protect the DB from missing-key stampedes (common for bots probing IDs). Keep TTL short to limit false negatives after creates."
      }
    ],
    "redis": [
      {
        "type": "h",
        "text": "Key naming & TTLs"
      },
      {
        "type": "p",
        "text": "Namespace keys (svc:user:42), always set TTL on cache keys, and avoid KEYS * in prod. Use SCAN for ops. Monitor eviction causes."
      }
    ],
    "rate-limiter": [
      {
        "type": "h",
        "text": "HTTP semantics"
      },
      {
        "type": "p",
        "text": "Return 429 with Retry-After. Make limit checks cheap and local when possible; reserve Redis for global quotas that must be exact."
      }
    ],
    "circuit-breaker": [
      {
        "type": "h",
        "text": "Bulkheads"
      },
      {
        "type": "p",
        "text": "Combine breakers with separate thread/connection pools per dependency so one slow vendor cannot exhaust your whole worker pool."
      }
    ],
    "distributed-lock": [
      {
        "type": "h",
        "text": "Fencing token pattern"
      },
      {
        "type": "p",
        "text": "Every lock acquisition returns a monotonically increasing token; storage rejects writes with older tokens. This closes the \"lock expired but process still writing\" hole."
      }
    ],
    "kafka": [
      {
        "type": "h",
        "text": "Poison pill handling"
      },
      {
        "type": "p",
        "text": "Deserialization errors should go to a DLQ topic after N attempts, not block the partition forever. Include headers with exception and original payload id."
      }
    ],
    "rabbitmq": [
      {
        "type": "h",
        "text": "Prefetch tuning"
      },
      {
        "type": "p",
        "text": "Prefetch=1 maximizes fairness for slow consumers; higher prefetch improves throughput for fast handlers. Measure consumer utilization."
      }
    ],
    "docker": [
      {
        "type": "h",
        "text": "Layer caching for CI"
      },
      {
        "type": "p",
        "text": "Order Dockerfile from least to most frequently changing; use multi-stage builds to keep runtime images small and free of compilers/secrets."
      }
    ],
    "kubernetes": [
      {
        "type": "h",
        "text": "Rollout safety"
      },
      {
        "type": "ul",
        "items": [
          "Readiness probes before traffic.",
          "PodDisruptionBudgets for voluntary evictions.",
          "Resource requests/limits to enable sane scheduling.",
          "MaxUnavailable on deployments."
        ]
      }
    ],
    "microservices": [
      {
        "type": "h",
        "text": "Contract testing"
      },
      {
        "type": "p",
        "text": "Consumer-driven contracts catch breaking API changes before production. Prefer versioned APIs and additive changes over hard cuts."
      }
    ],
    "saga": [
      {
        "type": "h",
        "text": "Outbox pattern pairing"
      },
      {
        "type": "p",
        "text": "Persist state change + outbound event in one DB transaction, then publish asynchronously. Prevents \"DB committed but event lost\" saga bugs."
      }
    ],
    "cqrs": [
      {
        "type": "h",
        "text": "When CQRS is overkill"
      },
      {
        "type": "p",
        "text": "Simple CRUD with symmetric read/write models gains little. Introduce CQRS when read shapes diverge hard or scale independently from writes."
      }
    ],
    "event-sourcing": [
      {
        "type": "h",
        "text": "GDPR / erase requests"
      },
      {
        "type": "p",
        "text": "Immutable events conflict with \"delete my data.\" Strategies: crypto-shredding (delete keys), restricted projections, or careful legal bases. Mention this maturity signal in interviews."
      }
    ],
    "observability": [
      {
        "type": "h",
        "text": "Exemplars & trace links"
      },
      {
        "type": "p",
        "text": "Link a metric spike to example traces and logs via correlation IDs. Dashboards without drill-down slow incident response."
      }
    ],
    "structured-logging": [
      {
        "type": "h",
        "text": "Sampling & cost"
      },
      {
        "type": "p",
        "text": "High-volume debug logs need sampling or dynamic enablement. Index only fields you query; the rest stays in raw storage."
      }
    ],
    "prometheus": [
      {
        "type": "h",
        "text": "Histogram bucket choice"
      },
      {
        "type": "p",
        "text": "Buckets must cover your SLO latency range. Wrong buckets make quantiles useless. Review buckets when SLOs change."
      }
    ],
    "grafana": [
      {
        "type": "h",
        "text": "Runbook links"
      },
      {
        "type": "p",
        "text": "Every critical alert panel should link to a runbook: symptoms → checks → mitigations → escalation."
      }
    ],
    "opentelemetry": [
      {
        "type": "h",
        "text": "Auto vs manual instrumentation"
      },
      {
        "type": "p",
        "text": "Auto-instrument frameworks for baseline traces; manually span business operations (reserve inventory, charge card) where the real meaning lives."
      }
    ]
  },
  "behavioral": {
    "star-method": [
      {
        "type": "h",
        "text": "Timing practice"
      },
      {
        "type": "p",
        "text": "Record yourself. Cut situation to 2–3 sentences. Spend time on decisions, trade-offs, and collaboration — that is what interviewers grade."
      }
    ],
    "ownership": [
      {
        "type": "h",
        "text": "Phrase bank"
      },
      {
        "type": "ul",
        "items": [
          "\"I noticed…\"",
          "\"I took responsibility for…\"",
          "\"I aligned stakeholders by…\"",
          "\"The measurable outcome was…\""
        ]
      }
    ],
    "production-incident": [
      {
        "type": "h",
        "text": "Comms cadence"
      },
      {
        "type": "p",
        "text": "Say how often you updated stakeholders (e.g., every 15 minutes), what you said when unknown, and how you prevented rumor/panic."
      }
    ],
    "failure-and-learning": [
      {
        "type": "h",
        "text": "Show the scar tissue"
      },
      {
        "type": "p",
        "text": "End with a process change: checklist, test, alert, or design review — proof the learning stuck."
      }
    ]
  }
};

export const getDepthEnrichment = (trackId, topicId) =>
  depthEnrichments[trackId]?.[topicId] || null;
